import React, { useState } from 'react';
import styles from './CollapsiblePanel.module.css';

interface CollapsiblePanelProps {
  header: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  isDay?: boolean;
}

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  header,
  children,
  defaultOpen = false,
  onToggle,
  isDay = true
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Essential for nested components

    const target = e.target as HTMLElement;

    // Don't toggle if an interactive element (like a checkbox) was clicked
    const interactiveNode = target.closest('button, a, input, select, textarea, label, [role="button"]');

    // If the click was on an interactive element inside the trigger, don't toggle
    // Unless the interactive element is the trigger itself
    if (interactiveNode && interactiveNode !== e.currentTarget && e.currentTarget.contains(interactiveNode)) {
      return;
    }

    const nextState = !isOpen;
    setIsOpen(nextState);
    onToggle?.(nextState);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const nextState = !isOpen;
      setIsOpen(nextState);
      onToggle?.(nextState);
    }
  };

  return (
    <div className={`${styles.container} ${!isDay ? styles.dark : ''} ${isOpen ? styles.isOpen : ''}`}>
      <div
        className={styles.trigger}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <div className={styles.headerSlot}>{header}</div>
        <svg
          className={styles.chevron}
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelInner}>{children}</div>
      </div>
    </div>
  );
};

export default CollapsiblePanel;
